import { Component, OnInit, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthCampaign, GrowthContentAsset, GrowthLeadCaptureForm, GrowthLeadQualificationRule, GrowthMarketingService, CampaignStatus, ContentStatus } from './growth-marketing.service';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { isEmailSmsChannel } from '../notifications/notification.utils';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { buildGrowthMarketingChannelOptions } from './growth-channel.constants';
import { combineUtcDateAndTime, splitUtcDateTime } from './growth-datetime.util';
import { CAMPAIGN_STATUS_I18N_KEYS, CONTENT_STATUS_I18N_KEYS, isCampaignAttachableContentStatus, normalizeCampaignStatus, normalizeContentStatus } from './growth-status.util';

type TabKey = 'campaigns' | 'content' | 'leadCapture' | 'rules';

interface ChannelMixRow {
  channelKey: string;
  weightPercent: number;
}

interface BudgetRow {
  category: string;
  amount: number;
  currency: string;
}

interface AbVariantRow {
  variantKey: string;
  title: string;
  allocationPercent: number;
}

interface CampaignAttachRow {
  contentAssetId: string;
  variantKey: string;
  channelKey: string;
  pending?: boolean;
}

interface LeadFormFieldRow {
  key: string;
  label: string;
  fieldType: string;
  required: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-marketing-campaigns',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    GrowthSegmentSelectorComponent,
    GrowthContentAssetSelectorComponent,
    CurrencySelectorComponent,
    JalaliDateInputComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    TabsModule,
    AinDatePipe,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.marketing.campaigns.title')"
      [description]="t('growth.marketing.campaigns.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.marketing.campaigns.help.page.title'"
        [descriptionKey]="'growth.marketing.campaigns.help.page.description'"
        [bulletKeys]="[
          'growth.marketing.campaigns.help.page.bullets.approval',
          'growth.marketing.campaigns.help.page.bullets.consent',
          'growth.marketing.campaigns.help.page.bullets.structured'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="loadAll()"></button>
      </div>

      <p-tabs [value]="activeTab()" (valueChange)="onTabChanged($event)">
        <p-tablist>
          <p-tab value="campaigns">{{ t('growth.marketing.campaigns.tabs.campaigns') }}</p-tab>
          <p-tab value="content">{{ t('growth.marketing.campaigns.tabs.content') }}</p-tab>
          <p-tab value="leadCapture">{{ t('growth.marketing.campaigns.tabs.leadCapture') }}</p-tab>
          <p-tab value="rules">{{ t('growth.marketing.campaigns.tabs.rules') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="campaigns">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.marketing.campaigns.sections.campaignBuilder') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.marketing.campaigns.help.builder.title'"
                  [descriptionKey]="'growth.marketing.campaigns.help.builder.description'"
                  [bulletKeys]="[
                    'growth.marketing.campaigns.help.builder.bullets.channelMix',
                    'growth.marketing.campaigns.help.builder.bullets.approvalGate',
                    'growth.marketing.campaigns.help.builder.bullets.launch'
                  ]" />
              </div>
              <div class="toolbar">
                <a pButton [routerLink]="['/growth/marketing/campaigns/new']" [label]="t('growth.marketing.campaigns.actions.newCampaign')"></a>
              </div>
              @if (loading()) {
                <ain-state type="loading" [title]="t('common.loading')" />
              } @else if (campaigns().length === 0) {
                <ain-state type="empty" [title]="t('growth.marketing.campaigns.empty')" />
              } @else {
                <p-table [value]="campaigns()" [rows]="10" [paginator]="true">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.marketing.campaigns.columns.name') }}</th>
                      <th>{{ t('growth.marketing.campaigns.columns.objective') }}</th>
                      <th>{{ t('growth.marketing.campaigns.columns.status') }}</th>
                      <th>{{ t('growth.marketing.campaigns.columns.schedule') }}</th>
                      <th>{{ t('growth.marketing.campaigns.columns.actions') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.name }}</td>
                      <td>{{ row.objective }}</td>
                      <td>{{ campaignStatusLabel(row.status) }}</td>
                      <td>{{ row.scheduleAtUtc ? (row.scheduleAtUtc | ainDate:'datetime') : '-' }}</td>
                      <td class="actions">
                        <a pButton size="small" [routerLink]="['/growth/marketing/campaigns', row.id]" [label]="t('common.edit')"></a>
                        <button pButton type="button" size="small" severity="secondary" [label]="t('growth.marketing.campaigns.actions.approve')" (click)="approveCampaign(row)"></button>
                        <button pButton type="button" size="small" [disabled]="!canLaunchCampaign(row)" [label]="t('growth.marketing.campaigns.actions.launch')" (click)="openLaunchDialog(row)"></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </section>
          </p-tabpanel>

          <p-tabpanel value="content">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.marketing.campaigns.sections.contentAssets') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.marketing.campaigns.help.content.title'"
                  [descriptionKey]="'growth.marketing.campaigns.help.content.description'"
                  [bulletKeys]="[
                    'growth.marketing.campaigns.help.content.bullets.lifecycle',
                    'growth.marketing.campaigns.help.content.bullets.citation',
                    'growth.marketing.campaigns.help.content.bullets.publish',
                    'growth.marketing.campaigns.help.content.bullets.socialBoundary'
                  ]" />
              </div>
              <div class="toolbar">
                <a
                  pButton
                  routerLink="/growth/marketing/content-assets/new"
                  [label]="t('growth.marketing.campaigns.actions.newContent')"></a>
              </div>
              <p-table [value]="contentAssets()" [rows]="10" [paginator]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.marketing.campaigns.columns.contentTitle') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.contentType') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.status') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>
                      <a class="link" [routerLink]="['/growth/marketing/content-assets', row.id]">{{ row.title }}</a>
                    </td>
                    <td>{{ row.contentType }}</td>
                    <td>{{ contentStatusLabel(row.status) }}</td>
                    <td class="actions">
                      <a pButton type="button" size="small" [label]="t('common.edit')" [routerLink]="['/growth/marketing/content-assets', row.id]"></a>
                      <button pButton type="button" size="small" severity="secondary" [disabled]="!canSubmitContent(row)" [label]="t('growth.marketing.campaigns.actions.submitForReview')" (click)="submitContent(row)"></button>
                      <button pButton type="button" size="small" [disabled]="!canApproveContent(row)" [label]="t('growth.marketing.campaigns.actions.approve')" (click)="approveContent(row)"></button>
                      <button pButton type="button" size="small" [disabled]="!canRejectContent(row)" [label]="t('growth.marketing.campaigns.actions.reject')" severity="danger" (click)="rejectContent(row)"></button>
                      <button pButton type="button" size="small" severity="help" [disabled]="!canScheduleContent(row)" [label]="t('growth.marketing.campaigns.actions.schedule')" (click)="openScheduleDialog(row)"></button>
                      <button pButton type="button" size="small" [disabled]="!canPublishContent(row)" [label]="t('growth.marketing.campaigns.actions.publish')" (click)="openPublishDialog(row)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="leadCapture">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.marketing.campaigns.sections.leadCapture') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.marketing.campaigns.help.leadCapture.title'"
                  [descriptionKey]="'growth.marketing.campaigns.help.leadCapture.description'"
                  [bulletKeys]="[
                    'growth.marketing.campaigns.help.leadCapture.bullets.forms',
                    'growth.marketing.campaigns.help.leadCapture.bullets.consent',
                    'growth.marketing.campaigns.help.leadCapture.bullets.leadCreation'
                  ]" />
              </div>
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.marketing.campaigns.actions.newLeadForm')" (click)="openLeadFormDialog()"></button>
              </div>
              <p-table [value]="leadForms()" [rows]="10" [paginator]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.marketing.campaigns.columns.formKey') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.consentPurpose') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.formKey }}</td>
                    <td>{{ row.consentPurposeKey }}</td>
                    <td>{{ row.status }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="rules">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.marketing.campaigns.sections.rules') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.marketing.campaigns.help.rules.title'"
                  [descriptionKey]="'growth.marketing.campaigns.help.rules.description'"
                  [bulletKeys]="[
                    'growth.marketing.campaigns.help.rules.bullets.trigger',
                    'growth.marketing.campaigns.help.rules.bullets.intent',
                    'growth.marketing.campaigns.help.rules.bullets.dedupe'
                  ]" />
              </div>
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.marketing.campaigns.actions.newRule')" (click)="openRuleDialog()"></button>
              </div>
              <p-table [value]="rules()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.marketing.campaigns.columns.ruleName') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.ruleStatus') }}</th>
                    <th>{{ t('growth.marketing.campaigns.columns.autoQualify') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.name }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.autoQualify ? t('common.yes') : t('common.no') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.campaign')" [visible]="campaignDialogVisible()" (visibleChange)="campaignDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(920px,96vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.name')"><input pInputText [(ngModel)]="campaignModel.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.objective')"><input pInputText [(ngModel)]="campaignModel.objective" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.purpose')">
            <p-select [options]="purposeOptions" optionLabel="label" optionValue="value" [(ngModel)]="campaignModel.purposeKey" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.segment')">
            <ain-growth-segment-selector [(ngModel)]="campaignModel.segmentId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleDate')"><ain-date-input [(ngModel)]="campaignModel.scheduleDateUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleTime')"><input type="time" [(ngModel)]="campaignModel.scheduleTimeUtc" /></ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.marketing.campaigns.sections.channelMix') }}</h4></div>
        @for (row of campaignChannelMix; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.weightPercent')"><p-inputNumber [(ngModel)]="row.weightPercent" [min]="0" [max]="100" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeCampaignChannel($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addCampaignChannel()"></button>

        <div class="section-title"><h4>{{ t('growth.marketing.campaigns.sections.budget') }}</h4></div>
        @for (row of campaignBudgetRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.budgetCategory')"><input pInputText [(ngModel)]="row.category" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.amount')"><p-inputNumber [(ngModel)]="row.amount" [min]="0" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.currency')"><ain-currency-selector [(ngModel)]="row.currency" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeCampaignBudget($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addCampaignBudget()"></button>

        <div class="section-title"><h4>{{ t('growth.marketing.campaigns.sections.abVariants') }}</h4></div>
        @for (row of campaignVariants; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.variantKey')"><input pInputText [(ngModel)]="row.variantKey" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.variantTitle')"><input pInputText [(ngModel)]="row.title" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.allocationPercent')"><p-inputNumber [(ngModel)]="row.allocationPercent" [min]="0" [max]="100" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeCampaignVariant($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addCampaignVariant()"></button>

        <div class="section-title"><h4>{{ t('growth.marketing.campaigns.sections.attachContent') }}</h4></div>
        <div class="row-grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.approvedContent')">
            <p-select [options]="approvedContentOptions()" optionLabel="label" optionValue="value" [(ngModel)]="selectedAttachContentId" appendTo="body" [showClear]="true" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.variantKey')"><input pInputText [(ngModel)]="attachVariantKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')">
            <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="attachChannelKey" appendTo="body" />
          </ain-form-field>
          <button pButton type="button" [label]="t('growth.marketing.campaigns.actions.attach')" (click)="attachSelectedContent()"></button>
        </div>
        @if (campaignAttachedRows.length > 0) {
          <p-table [value]="campaignAttachedRows" class="attached-content-table">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.marketing.campaigns.columns.contentTitle') }}</th>
                <th>{{ t('growth.marketing.campaigns.fields.variantKey') }}</th>
                <th>{{ t('growth.marketing.campaigns.fields.channel') }}</th>
                <th>{{ t('growth.marketing.campaigns.columns.status') }}</th>
                <th>{{ t('growth.marketing.campaigns.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
              <tr>
                <td>{{ contentTitleById(row.contentAssetId) }}</td>
                <td>{{ row.variantKey }}</td>
                <td>{{ channelLabel(row.channelKey) }}</td>
                <td>{{ row.pending ? t('growth.marketing.campaigns.attach.pending') : t('growth.marketing.campaigns.attach.saved') }}</td>
                <td>
                  @if (row.pending) {
                    <button pButton type="button" size="small" severity="danger" icon="pi pi-trash" (click)="removePendingAttach(rowIndex)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="campaignDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveCampaign()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.leadForm')" [visible]="leadFormDialogVisible()" (visibleChange)="leadFormDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(840px,94vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.formKey')"><input pInputText [(ngModel)]="leadFormModel.formKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.campaign')">
            <p-select [options]="campaignOptions()" optionLabel="label" optionValue="value" [(ngModel)]="leadFormModel.campaignId" [showClear]="true" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.approvedContent')">
            <ain-growth-content-asset-selector [(ngModel)]="leadFormModel.contentAssetId" statusFilter="Approved" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.consentPurpose')"><p-select [options]="purposeOptions" optionLabel="label" optionValue="value" [(ngModel)]="leadFormModel.consentPurposeKey" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.status')">
            <p-select [options]="leadFormStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="leadFormModel.status" appendTo="body" />
          </ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.marketing.campaigns.sections.formFields') }}</h4></div>
        @for (row of leadFieldRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.fieldKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.fieldLabel')"><input pInputText [(ngModel)]="row.label" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.fieldType')">
              <p-select [options]="leadFieldTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.fieldType" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.required')"><p-select [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.required" appendTo="body" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeLeadField($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addLeadField()"></button>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="leadFormDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveLeadForm()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.rule')" [visible]="ruleDialogVisible()" (visibleChange)="ruleDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(760px,92vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.ruleName')"><input pInputText [(ngModel)]="ruleModel.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.minEngagementScore')"><p-inputNumber [(ngModel)]="ruleModel.minEngagementScore" [min]="0" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.autoQualify')"><p-select [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleModel.autoQualify" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.ruleStatus')">
            <p-select [options]="ruleStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleModel.status" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.triggerEvents')"><input pInputText [(ngModel)]="ruleModel.triggerEventsCsv" placeholder="form_submit,request_demo,comment" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.intentKeywords')"><input pInputText [(ngModel)]="ruleModel.intentKeywordsCsv" placeholder="demo,quote,pricing" /></ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="ruleDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveRule()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.scheduleContent')" [visible]="scheduleDialogVisible()" (visibleChange)="scheduleDialogVisible.set($event)" [modal]="true">
        <ain-info-hint
          [titleKey]="'growth.marketing.campaigns.help.scheduleContent.title'"
          [descriptionKey]="'growth.marketing.campaigns.help.scheduleContent.description'" />
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')">
            <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="scheduleModel.channelKey" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleDate')"><ain-date-input [(ngModel)]="scheduleModel.scheduleDateUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleTime')"><input type="time" [(ngModel)]="scheduleModel.scheduleTimeUtc" /></ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="scheduleDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('growth.marketing.campaigns.actions.schedule')" (click)="confirmScheduleContent()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.launchCampaign')" [visible]="launchDialogVisible()" (visibleChange)="launchDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(640px,94vw)' }">
        <ain-info-hint
          [titleKey]="'growth.marketing.campaigns.help.launch.title'"
          [descriptionKey]="'growth.marketing.campaigns.help.launch.description'" />
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.segment')">
            <ain-growth-segment-selector [(ngModel)]="launchModel.segmentId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')">
            <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="launchModel.channelKey" appendTo="body" />
          </ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="launchDialogVisible.set(false)"></button>
          <button pButton type="button" [disabled]="!launchModel.segmentId" [label]="t('growth.marketing.campaigns.actions.launch')" (click)="confirmLaunchCampaign()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.marketing.campaigns.dialogs.publishContent')" [visible]="publishDialogVisible()" (visibleChange)="publishDialogVisible.set($event)" [modal]="true">
        <ain-info-hint
          [titleKey]="'growth.marketing.campaigns.help.publishContent.title'"
          [descriptionKey]="'growth.marketing.campaigns.help.publishContent.description'"
          [bulletKeys]="['growth.marketing.campaigns.help.publishContent.bullets.social', 'growth.marketing.campaigns.help.publishContent.bullets.emailSmsDistribution', 'growth.marketing.campaigns.help.publishContent.bullets.approval']" />
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')">
            <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="publishModel.channelKey" appendTo="body" />
          </ain-form-field>
        </div>
        @if (isEmailSmsPublishChannel()) {
          <p class="distribution-hint">{{ t('growth.marketing.campaigns.publish.emailSmsHint') }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="publishDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="isEmailSmsPublishChannel() ? t('growth.marketing.campaigns.actions.openDistribution') : t('growth.marketing.campaigns.actions.publish')" (click)="confirmPublishContent()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .attached-content-table { margin-top: 8px; }
    .link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .link:hover { text-decoration: underline; }
  `]
})
export class GrowthMarketingCampaignsComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly router = inject(Router);
  private readonly i18n = inject(LocalizationService);

  readonly activeTab = signal<TabKey>('campaigns');
  readonly loading = signal(false);
  readonly campaigns = signal<GrowthCampaign[]>([]);
  readonly contentAssets = signal<GrowthContentAsset[]>([]);
  readonly leadForms = signal<GrowthLeadCaptureForm[]>([]);
  readonly rules = signal<GrowthLeadQualificationRule[]>([]);

  readonly campaignDialogVisible = signal(false);
  readonly leadFormDialogVisible = signal(false);
  readonly ruleDialogVisible = signal(false);
  readonly scheduleDialogVisible = signal(false);
  readonly publishDialogVisible = signal(false);
  readonly launchDialogVisible = signal(false);

  selectedCampaignId: string | null = null;
  selectedScheduleContentId: string | null = null;
  selectedPublishContentId: string | null = null;
  selectedAttachContentId: string | null = null;
  attachVariantKey = 'A';
  attachChannelKey = 'email';
  campaignAttachedRows: CampaignAttachRow[] = [];

  campaignModel = {
    name: '',
    objective: '',
    purposeKey: 'marketing',
    segmentId: null as string | null,
    scheduleDateUtc: null as string | null,
    scheduleTimeUtc: '09:00'
  };
  campaignChannelMix: ChannelMixRow[] = [{ channelKey: 'email', weightPercent: 60 }];
  campaignBudgetRows: BudgetRow[] = [{ category: 'default', amount: 1000, currency: 'USD' }];
  campaignVariants: AbVariantRow[] = [{ variantKey: 'A', title: 'Primary', allocationPercent: 50 }, { variantKey: 'B', title: 'Variant', allocationPercent: 50 }];

  selectedLaunchCampaignId: string | null = null;
  launchModel = {
    segmentId: null as string | null,
    channelKey: 'email'
  };

  scheduleModel = {
    channelKey: 'web',
    scheduleDateUtc: null as string | null,
    scheduleTimeUtc: '09:00'
  };
  publishModel = { channelKey: 'web' };

  leadFormModel = {
    formKey: '',
    campaignId: null as string | null,
    contentAssetId: null as string | null,
    consentPurposeKey: 'marketing',
    status: 'Active'
  };
  leadFieldRows: LeadFormFieldRow[] = [{ key: 'fullName', label: 'Full Name', fieldType: 'text', required: true }];

  ruleModel = {
    name: '',
    triggerEventsCsv: 'form_submit,request_demo,request_quote,request_contact',
    intentKeywordsCsv: 'demo,quote,pricing,contact',
    minEngagementScore: 0,
    autoQualify: true,
    status: 'Active'
  };

  get purposeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'marketing', label: this.t('growth.comms.purpose.marketing') },
      { value: 'service', label: this.t('growth.comms.purpose.service') },
      { value: 'transactional', label: this.t('growth.comms.purpose.transactional') }
    ];
  }

  get channelOptions(): Array<{ value: string; label: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  readonly leadFormStatusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Archived', label: 'Archived' }
  ];

  readonly ruleStatusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Archived', label: 'Archived' }
  ];

  readonly leadFieldTypeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Select' }
  ];

  readonly booleanOptions = [
    { value: true, label: 'True' },
    { value: false, label: 'False' }
  ];

  ngOnInit(): void {
    this.loadAll();
  }

  onTabChanged(value: string | number | undefined): void {
    if (value === 'campaigns' || value === 'content' || value === 'leadCapture' || value === 'rules')
      this.activeTab.set(value);
  }

  loadAll(): void {
    this.loading.set(true);
    this.service.listCampaigns().subscribe({ next: items => this.campaigns.set(items) });
    this.service.listContent().subscribe({ next: items => this.contentAssets.set(items) });
    this.service.listLeadCaptureForms().subscribe({ next: items => this.leadForms.set(items) });
    this.service.listLeadQualificationRules().subscribe({
      next: items => {
        this.rules.set(items);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  approvedContentOptions() {
    return this.contentAssets()
      .filter(item => isCampaignAttachableContentStatus(item.status))
      .map(item => ({ value: item.id, label: `${item.title} (${item.contentType})` }));
  }

  campaignOptions() {
    return this.campaigns().map(item => ({ value: item.id, label: item.name }));
  }

  openCampaignDialog(): void {
    this.selectedCampaignId = null;
    this.campaignModel = { name: '', objective: '', purposeKey: 'marketing', segmentId: null, scheduleDateUtc: null, scheduleTimeUtc: '09:00' };
    this.campaignChannelMix = [{ channelKey: 'email', weightPercent: 60 }];
    this.campaignBudgetRows = [{ category: 'default', amount: 1000, currency: 'USD' }];
    this.campaignVariants = [{ variantKey: 'A', title: 'Primary', allocationPercent: 50 }, { variantKey: 'B', title: 'Variant', allocationPercent: 50 }];
    this.selectedAttachContentId = null;
    this.attachVariantKey = 'A';
    this.attachChannelKey = 'email';
    this.campaignAttachedRows = [];
    this.campaignDialogVisible.set(true);
  }

  editCampaign(item: GrowthCampaign): void {
    this.selectedCampaignId = item.id;
    const scheduleSplit = splitUtcDateTime(item.scheduleAtUtc);
    this.campaignModel = {
      name: item.name,
      objective: item.objective,
      purposeKey: item.purposeKey,
      segmentId: item.segmentId,
      scheduleDateUtc: scheduleSplit.dateIso,
      scheduleTimeUtc: scheduleSplit.time
    };
    this.campaignChannelMix = this.parseChannelMix(item.channelMixJson);
    this.campaignBudgetRows = this.parseBudget(item.budgetJson);
    this.campaignVariants = this.parseVariants(item.abTestConfigJson);
    this.selectedAttachContentId = null;
    this.attachVariantKey = 'A';
    this.attachChannelKey = 'email';
    this.campaignAttachedRows = [];
    this.loadCampaignAttachedContents(item.id);
    this.campaignDialogVisible.set(true);
  }

  loadCampaignAttachedContents(campaignId: string): void {
    this.service.listCampaignContents(campaignId).subscribe({
      next: items => {
        this.campaignAttachedRows = items.map(item => ({
          contentAssetId: item.contentAssetId,
          variantKey: item.variantKey,
          channelKey: item.channelKey,
          pending: false
        }));
      }
    });
  }

  saveCampaign(): void {
    const payload = {
      id: this.selectedCampaignId,
      name: this.campaignModel.name,
      objective: this.campaignModel.objective,
      segmentId: this.campaignModel.segmentId,
      purposeKey: this.campaignModel.purposeKey,
      budgetJson: JSON.stringify(this.campaignBudgetRows),
      scheduleAtUtc: combineUtcDateAndTime(this.campaignModel.scheduleDateUtc, this.campaignModel.scheduleTimeUtc),
      channelMixJson: JSON.stringify(this.campaignChannelMix),
      abTestConfigJson: JSON.stringify(this.campaignVariants),
      simulationRequired: false,
      simulationRef: null
    };
    const request$ = this.selectedCampaignId ? this.service.updateCampaign(this.selectedCampaignId, payload) : this.service.createCampaign(payload);
    request$.subscribe({
      next: campaign => {
        const rowsToAttach = this.buildAttachRowsForSave();
        if (rowsToAttach.length === 0) {
          this.finishCampaignSave();
          return;
        }
        forkJoin(
          rowsToAttach.map(row => this.service.attachCampaignContent(campaign.id, {
            contentAssetId: row.contentAssetId,
            variantKey: row.variantKey,
            channelKey: row.channelKey
          }))
        ).subscribe({
          next: () => this.finishCampaignSave(),
          error: () => this.finishCampaignSave()
        });
      }
    });
  }

  private finishCampaignSave(): void {
    this.campaignDialogVisible.set(false);
    this.campaignAttachedRows = [];
    this.selectedAttachContentId = null;
    this.loadAll();
  }

  private buildAttachRowsForSave(): CampaignAttachRow[] {
    const rows = [...this.campaignAttachedRows.filter(row => row.pending)];
    if (this.selectedAttachContentId && !this.hasAttachRow(this.selectedAttachContentId, this.attachChannelKey || 'email', this.campaignAttachedRows)) {
      rows.push({
        contentAssetId: this.selectedAttachContentId,
        variantKey: this.attachVariantKey || 'A',
        channelKey: this.attachChannelKey || 'email',
        pending: true
      });
    }
    return rows;
  }

  approveCampaign(item: GrowthCampaign): void {
    this.service.approveCampaign(item.id).subscribe({ next: () => this.loadAll() });
  }

  openLaunchDialog(item: GrowthCampaign): void {
    const mix = this.parseChannelMix(item.channelMixJson);
    this.selectedLaunchCampaignId = item.id;
    this.launchModel = {
      segmentId: item.segmentId,
      channelKey: mix[0]?.channelKey ?? 'email'
    };
    this.launchDialogVisible.set(true);
  }

  confirmLaunchCampaign(): void {
    if (!this.selectedLaunchCampaignId || !this.launchModel.segmentId)
      return;

    this.launchDialogVisible.set(false);
    void this.router.navigate(['/growth/comms/distributions'], {
      queryParams: {
        campaignId: this.selectedLaunchCampaignId,
        segmentId: this.launchModel.segmentId,
        channelKey: this.launchModel.channelKey || 'email'
      }
    });
    this.selectedLaunchCampaignId = null;
  }

  attachSelectedContent(): void {
    if (!this.selectedAttachContentId)
      return;

    const row: CampaignAttachRow = {
      contentAssetId: this.selectedAttachContentId,
      variantKey: this.attachVariantKey || 'A',
      channelKey: this.attachChannelKey || 'email'
    };

    if (!this.selectedCampaignId) {
      if (!this.hasAttachRow(row.contentAssetId, row.channelKey, this.campaignAttachedRows)) {
        this.campaignAttachedRows = [...this.campaignAttachedRows, { ...row, pending: true }];
      }
      this.selectedAttachContentId = null;
      return;
    }

    if (this.hasAttachRow(row.contentAssetId, row.channelKey, this.campaignAttachedRows))
      return;

    this.service.attachCampaignContent(this.selectedCampaignId, row).subscribe({
      next: () => {
        this.selectedAttachContentId = null;
        this.loadCampaignAttachedContents(this.selectedCampaignId!);
        this.loadAll();
      }
    });
  }

  removePendingAttach(index: number): void {
    const row = this.campaignAttachedRows[index];
    if (!row?.pending)
      return;
    this.campaignAttachedRows = this.campaignAttachedRows.filter((_, i) => i !== index);
  }

  contentTitleById(contentAssetId: string): string {
    const asset = this.contentAssets().find(item => item.id === contentAssetId);
    return asset ? `${asset.title} (${asset.contentType})` : contentAssetId;
  }

  channelLabel(channelKey: string): string {
    return this.t(`growth.channels.${channelKey}`);
  }

  private hasAttachRow(contentAssetId: string, channelKey: string, rows: CampaignAttachRow[]): boolean {
    return rows.some(row => row.contentAssetId === contentAssetId && row.channelKey === channelKey);
  }

  submitContent(item: GrowthContentAsset): void {
    this.service.submitContentForReview(item.id).subscribe({ next: () => this.loadAll() });
  }

  approveContent(item: GrowthContentAsset): void {
    this.service.approveContent(item.id).subscribe({ next: () => this.loadAll() });
  }

  rejectContent(item: GrowthContentAsset): void {
    this.service.rejectContent(item.id, 'Requires factuality revision').subscribe({ next: () => this.loadAll() });
  }

  canLaunchCampaign(item: GrowthCampaign): boolean {
    return normalizeCampaignStatus(item.status) === 'Approved';
  }

  contentStatusLabel(status: ContentStatus | unknown): string {
    const normalized = normalizeContentStatus(status);
    return this.t(CONTENT_STATUS_I18N_KEYS[normalized]);
  }

  campaignStatusLabel(status: CampaignStatus | unknown): string {
    const normalized = normalizeCampaignStatus(status);
    return this.t(CAMPAIGN_STATUS_I18N_KEYS[normalized]);
  }

  canSubmitContent(item: GrowthContentAsset): boolean {
    const status = normalizeContentStatus(item.status);
    return status === 'Draft' || status === 'AiGenerated' || status === 'Rejected';
  }

  canApproveContent(item: GrowthContentAsset): boolean {
    return normalizeContentStatus(item.status) === 'InReview';
  }

  canRejectContent(item: GrowthContentAsset): boolean {
    return normalizeContentStatus(item.status) === 'InReview';
  }

  canScheduleContent(item: GrowthContentAsset): boolean {
    return normalizeContentStatus(item.status) === 'Approved';
  }

  canPublishContent(item: GrowthContentAsset): boolean {
    const status = normalizeContentStatus(item.status);
    return status === 'Approved' || status === 'Scheduled';
  }

  openScheduleDialog(item: GrowthContentAsset): void {
    this.selectedScheduleContentId = item.id;
    this.scheduleModel = { channelKey: 'web', scheduleDateUtc: null, scheduleTimeUtc: '09:00' };
    this.scheduleDialogVisible.set(true);
  }

  confirmScheduleContent(): void {
    if (!this.selectedScheduleContentId) return;
    const scheduledAtUtc = combineUtcDateAndTime(this.scheduleModel.scheduleDateUtc, this.scheduleModel.scheduleTimeUtc);
    if (!scheduledAtUtc) return;
    this.service.scheduleContent(this.selectedScheduleContentId, {
      scheduledAtUtc,
      channelKey: this.scheduleModel.channelKey
    }).subscribe({
      next: () => {
        this.scheduleDialogVisible.set(false);
        this.loadAll();
      }
    });
  }

  openPublishDialog(item: GrowthContentAsset): void {
    this.selectedPublishContentId = item.id;
    this.publishModel = { channelKey: item.contentType === 'social-post' ? 'social' : 'web' };
    this.publishDialogVisible.set(true);
  }

  confirmPublishContent(): void {
    if (!this.selectedPublishContentId) return;
    if (isEmailSmsChannel(this.publishModel.channelKey)) {
      this.publishDialogVisible.set(false);
      void this.router.navigate(['/growth/comms/distributions'], {
        queryParams: {
          contentAssetId: this.selectedPublishContentId,
          channelKey: this.publishModel.channelKey
        }
      });
      this.selectedPublishContentId = null;
      return;
    }
    this.service.publishContent(this.selectedPublishContentId, {
      channelKey: this.publishModel.channelKey,
      idempotencyKey: `content-publish-${this.selectedPublishContentId}-${Date.now()}`
    }).subscribe({
      next: () => {
        this.publishDialogVisible.set(false);
        this.loadAll();
      }
    });
  }

  isEmailSmsPublishChannel(): boolean {
    return isEmailSmsChannel(this.publishModel.channelKey);
  }

  openLeadFormDialog(): void {
    this.leadFormModel = { formKey: '', campaignId: null, contentAssetId: null, consentPurposeKey: 'marketing', status: 'Active' };
    this.leadFieldRows = [{ key: 'fullName', label: 'Full Name', fieldType: 'text', required: true }];
    this.leadFormDialogVisible.set(true);
  }

  saveLeadForm(): void {
    const payload = {
      formKey: this.leadFormModel.formKey,
      campaignId: this.leadFormModel.campaignId,
      contentAssetId: this.leadFormModel.contentAssetId,
      fieldsJson: JSON.stringify(this.leadFieldRows),
      consentPurposeKey: this.leadFormModel.consentPurposeKey,
      status: this.leadFormModel.status
    };
    this.service.createLeadCaptureForm(payload).subscribe({
      next: () => {
        this.leadFormDialogVisible.set(false);
        this.loadAll();
      }
    });
  }

  openRuleDialog(): void {
    this.ruleModel = {
      name: '',
      triggerEventsCsv: 'form_submit,request_demo,request_quote,request_contact',
      intentKeywordsCsv: 'demo,quote,pricing,contact',
      minEngagementScore: 0,
      autoQualify: true,
      status: 'Active'
    };
    this.ruleDialogVisible.set(true);
  }

  saveRule(): void {
    const payload = {
      name: this.ruleModel.name,
      triggerEventTypesJson: JSON.stringify(this.csvToArray(this.ruleModel.triggerEventsCsv)),
      intentKeywordsJson: JSON.stringify(this.csvToArray(this.ruleModel.intentKeywordsCsv)),
      minEngagementScore: this.ruleModel.minEngagementScore,
      autoQualify: this.ruleModel.autoQualify,
      dedupePolicyJson: JSON.stringify({ windowHours: 24 }),
      status: this.ruleModel.status
    };
    this.service.createLeadQualificationRule(payload).subscribe({
      next: () => {
        this.ruleDialogVisible.set(false);
        this.loadAll();
      }
    });
  }

  addCampaignChannel(): void { this.campaignChannelMix.push({ channelKey: 'email', weightPercent: 0 }); }
  removeCampaignChannel(index: number): void { this.campaignChannelMix.splice(index, 1); }
  addCampaignBudget(): void { this.campaignBudgetRows.push({ category: '', amount: 0, currency: 'USD' }); }
  removeCampaignBudget(index: number): void { this.campaignBudgetRows.splice(index, 1); }
  addCampaignVariant(): void { this.campaignVariants.push({ variantKey: '', title: '', allocationPercent: 0 }); }
  removeCampaignVariant(index: number): void { this.campaignVariants.splice(index, 1); }
  addLeadField(): void { this.leadFieldRows.push({ key: '', label: '', fieldType: 'text', required: false }); }
  removeLeadField(index: number): void { this.leadFieldRows.splice(index, 1); }

  private parseChannelMix(json: string): ChannelMixRow[] {
    try { return JSON.parse(json) as ChannelMixRow[]; } catch { return [{ channelKey: 'email', weightPercent: 60 }]; }
  }

  private parseBudget(json: string): BudgetRow[] {
    try { return JSON.parse(json) as BudgetRow[]; } catch { return [{ category: 'default', amount: 1000, currency: 'USD' }]; }
  }

  private parseVariants(json: string): AbVariantRow[] {
    try { return JSON.parse(json) as AbVariantRow[]; } catch { return [{ variantKey: 'A', title: 'Primary', allocationPercent: 50 }, { variantKey: 'B', title: 'Variant', allocationPercent: 50 }]; }
  }

  private csvToArray(value: string): string[] {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
